import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Tabs,
  Tab,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  LocalShipping as ShipIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import affiliateService from '../services/affiliateService';

function AffiliateProductRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [statusData, setStatusData] = useState({
    status: '',
    trackingNumber: '',
    adminNotes: '',
  });

  useEffect(() => {
    loadRequests(currentTab);
  }, [currentTab]);

  const loadRequests = async (status) => {
    try {
      setLoading(true);
      const data = await affiliateService.getProductRequests(status === 'all' ? null : status);
      setRequests(data.data || []);
      setFilteredRequests(data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des demandes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredRequests(requests);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = requests.filter(request => 
        request.affiliate?.user?.first_name?.toLowerCase().includes(term) ||
        request.affiliate?.user?.last_name?.toLowerCase().includes(term) ||
        request.affiliate?.affiliateCode?.toLowerCase().includes(term) ||
        request.product?.name?.toLowerCase().includes(term) ||
        request.campaign?.title?.toLowerCase().includes(term)
      );
      setFilteredRequests(filtered);
    }
  }, [searchTerm, requests]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleOpenDialog = (request) => {
    setSelectedRequest(request);
    setStatusData({
      status: request.status,
      trackingNumber: request.trackingNumber || '',
      adminNotes: request.adminNotes || '',
    });
    setOpenDialog(true);
  };

  const handleSaveStatus = async () => {
    try {
      await affiliateService.updateProductRequestStatus(selectedRequest.id, statusData);
      showSnackbar('Statut mis à jour');
      setOpenDialog(false);
      loadRequests(currentTab);
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Erreur lors de la mise à jour';
      showSnackbar(errorMessage, 'error');
      
      // Si la demande n'existe plus, recharger la liste
      if (error?.response?.status === 404) {
        setOpenDialog(false);
        loadRequests(currentTab);
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleViewUser = (userId) => {
    navigate(`/users?userId=${userId}&openModal=true`);
  };

  const getStatusChip = (status) => {
    const config = {
      pending: { label: 'En attente', color: 'warning' },
      approved: { label: 'Approuvée', color: 'info' },
      shipped: { label: 'Expédiée', color: 'primary' },
      delivered: { label: 'Livrée', color: 'success' },
      rejected: { label: 'Rejetée', color: 'error' },
    };
    return <Chip label={config[status]?.label || status} color={config[status]?.color || 'default'} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Demandes de produits - Affiliés
      </Typography>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par affilié, produit, campagne..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      </Box>

      <Tabs value={currentTab} onChange={(e, value) => setCurrentTab(value)} sx={{ mb: 3 }}>
        <Tab label="En attente" value="pending" />
        <Tab label="Approuvées" value="approved" />
        <Tab label="Expédiées" value="shipped" />
        <Tab label="Livrées" value="delivered" />
        <Tab label="Toutes" value="all" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Affilié</TableCell>
              <TableCell>Produit</TableCell>
              <TableCell>Campagne</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Avis</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    {searchTerm ? 'Aucun résultat trouvé' : 'Aucune demande'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box>
                      <Typography variant="body2">
                        {request.affiliate?.user?.first_name} {request.affiliate?.user?.last_name}
                      </Typography>
                      <Chip 
                        label={request.affiliate?.affiliateCode} 
                        size="small" 
                        variant="outlined" 
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <IconButton 
                      size="small" 
                      color="info"
                      onClick={() => handleViewUser(request.affiliate?.userId)}
                      title="Voir le profil utilisateur"
                    >
                      <PersonIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>{request.product?.name}</TableCell>
                <TableCell>{request.campaign?.title}</TableCell>
                <TableCell>
                  {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                </TableCell>
                <TableCell>{getStatusChip(request.status)}</TableCell>
                <TableCell>
                  {request.review ? (
                    <Chip 
                      label={`⭐ ${request.review.rating}/5`} 
                      color={request.review.isPublished ? 'success' : 'default'}
                      size="small"
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(request)} color="primary">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog mise à jour statut */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mettre à jour la demande</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Affilié</Typography>
                <Typography>
                  {selectedRequest.affiliate?.user?.first_name} {selectedRequest.affiliate?.user?.last_name}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Produit</Typography>
                <Typography>{selectedRequest.product?.name}</Typography>
              </Grid>

              {selectedRequest.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Notes de l'affilié</Typography>
                  <Typography>{selectedRequest.notes}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={statusData.status}
                    label="Statut"
                    onChange={(e) => setStatusData({ ...statusData, status: e.target.value })}
                  >
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="approved">Approuvée</MenuItem>
                    <MenuItem value="shipped">Expédiée</MenuItem>
                    <MenuItem value="delivered">Livrée</MenuItem>
                    <MenuItem value="rejected">Rejetée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {(statusData.status === 'shipped' || statusData.status === 'delivered') && (
                <Grid item xs={12}>
                  <TextField
                    label="Numéro de suivi"
                    fullWidth
                    value={statusData.trackingNumber}
                    onChange={(e) => setStatusData({ ...statusData, trackingNumber: e.target.value })}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="Notes admin"
                  fullWidth
                  multiline
                  rows={3}
                  value={statusData.adminNotes}
                  onChange={(e) => setStatusData({ ...statusData, adminNotes: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button onClick={handleSaveStatus} variant="contained">
            Mettre à jour
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AffiliateProductRequests;

